import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiChevronRight, FiSearch } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import './Header.css';

// Mega-menu targets are a mix of in-app routes and sibling platform sites
// (crm / precision / edu, 见下面三个 *_ORIGIN 常量)。Router <Link> can't leave the
// SPA, so absolute URLs fall back to a plain anchor.
const isExternalUrl = (url) => /^https?:\/\//.test(url);

// 海外升学教育（VeyJet 巍杰致远）是独立站点 edu.infini-nova.com。
// 它的语言版本靠路径前缀区分：简体在根、繁体 /zh-Hant、英文 /en。
// 这里按当前界面语言拼出对应版本，别让繁体用户点进简体页。
// ⚠️ edu 站**没有日文版**，日文界面回落到英文（不是简体：日文用户读英文更顺）。
// 精密制造是独立站点（单页，靠 #锚点 分区）。子项直达对应板块，不再一律落在首页。
// 2026-08-17 从 precision.infini-nova.com 渲染后的 DOM 实际抓取的锚点：
//   #cnc      Precision Components & CNC Machining
//   #pm       Powder Metallurgy — Press & Sinter
//   #mim      Metal Injection Molding
//   #catalog  Our manufacturing lines（七条产线总览，"非标定制"落这里最合适）
//   其余未用到：#plastic #stamping #finish #robotics #services #why #contact
// ⚠️ 锚点由对方站点定义。那边改了 id，这里的链接会静默退化成"只打开首页"——
//    不会报错，所以改版后要手工点一遍。
// 🔴 2026-08-17 实测：带 #锚点 从外部进入 precision 站**不会滚动到板块**。
//    原因在对方站点，不在这里：它是 SPA，初始 HTML 只有空的 <div id="root">，
//    浏览器处理 fragment 的那一刻 #cnc 等元素还不存在，于是放弃滚动；
//    等 React 渲染完也没人再滚。（站内点击可用，是因为那时元素已存在。）
//    修复必须做在 precision 站：挂载后读 location.hash 再 scrollIntoView。
//    在我们这边**无法可靠绕过** —— 保留锚点是对的，对方修好即自动生效。
const PRECISION_ORIGIN = 'https://precision.infini-nova.com';

// 数字商务（域天星 CRM）。2026-08-17 从 crm.infininova.cn 改到 .com ——
// .cn 域名暂不启用（ICP 备案未办，见部署计划 §0.2）。
// ⚠️ 该子域当前**尚未解析**，上线前点它会打不开。
const CRM_ORIGIN = 'https://crm.infini-nova.com';

const EDU_ORIGIN = 'https://edu.infini-nova.com';
const EDU_LOCALE_PREFIX = { 'zh-CN': '', 'zh-TW': '/zh-Hant', en: '/en', ja: '/en' };

const eduUrl = (path = '', language = 'zh-CN') => {
  // i18next 可能给出 'zh-TW'、'en-US' 这类值,先精确匹配再退到主语言。
  const prefix =
    EDU_LOCALE_PREFIX[language] ??
    EDU_LOCALE_PREFIX[String(language).split('-')[0]] ??
    '';
  return `${EDU_ORIGIN}${prefix}${path}`;
};

const MenuLink = ({ to, className, children }) =>
  isExternalUrl(to) ? (
    <a href={to} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <Link to={to} className={className}>
      {children}
    </Link>
  );

const Header = () => {
  const { t, i18n } = useTranslation('common');
  // 绑定当前语言，菜单里直接 eduLink('/diagnosis') 即可
  const eduLink = (path) => eduUrl(path, i18n.language);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setIsSearchOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const aboutNews = t('news.about', { returnObjects: true });
  const insightsNews = t('news.insights', { returnObjects: true });

  const menuItems = [
    {
      label: t('nav.whatWeDo'),
      path: '/services',
      hasMegaMenu: true,
      megaMenu: {
        title: t('nav.menu.services.title'),
        link: '/services',
        columns: [
          {
            title: t('nav.menu.services.marketIntelligence'),
            titleLink: '/services/market-intelligence',
            items: [
              { label: t('nav.menu.services.consumerInsights'), path: '/services/market-insights' },
              { label: t('nav.menu.services.competitiveAnalysis'), path: '/services/competitive-analysis' },
              { label: t('nav.menu.services.businessStrategy'), path: '/services/business-strategy' },
              { label: t('nav.menu.services.performanceMonitoring'), path: '/services/performance-monitoring' }
            ]
          },
          {
            title: t('nav.menu.services.digitalCommerce'),
            titleLink: CRM_ORIGIN,
            subtitle: t('nav.menu.services.digitalCommerceSubtitle'),
            items: [
              { label: t('nav.menu.services.crmPlatform'), path: CRM_ORIGIN },
              { label: t('nav.menu.services.marketplaceEnablement'), path: '/services/marketplace-enablement' },
              { label: t('nav.menu.services.ecommercePlatform'), path: '/services/ecommerce-management' },
              { label: t('nav.menu.services.growthOptimisation'), path: '/services/growth-optimisation' }
            ]
          },
          {
            title: t('nav.menu.services.precisionManufacturing'),
            titleLink: PRECISION_ORIGIN,
            subtitle: t('nav.menu.services.precisionManufacturingSubtitle'),
            items: [
              { label: t('nav.menu.services.cnc'), path: `${PRECISION_ORIGIN}/#cnc` },
              { label: t('nav.menu.services.powderMetallurgy'), path: `${PRECISION_ORIGIN}/#pm` },
              { label: t('nav.menu.services.mim'), path: `${PRECISION_ORIGIN}/#mim` },
              { label: t('nav.menu.services.customPrecisionParts'), path: `${PRECISION_ORIGIN}/#catalog` }
            ]
          },
          {
            title: t('nav.menu.services.overseasEducation'),
            titleLink: eduLink(),
            subtitle: t('nav.menu.services.overseasEducationSubtitle'),
            items: [
              { label: t('nav.menu.services.eduDiagnosis'), path: eduLink('/diagnosis') },
              { label: t('nav.menu.services.eduYearly'), path: eduLink('/yearly') },
              { label: t('nav.menu.services.eduLongTerm'), path: eduLink('/long-term') },
              { label: t('nav.menu.services.eduApplication'), path: eduLink('/application') },
              { label: t('nav.menu.services.eduStem'), path: eduLink('/stem') }
            ]
          }
        ],
        bottomRow: [
          {
            title: t('nav.menu.services.technology'),
            titleLink: '/services/technology',
            items: [
              { label: t('nav.menu.services.cloudInfra'), path: '/services/cloud-infrastructure' },
              { label: t('nav.menu.services.analytics'), path: '/services/analytics' },
              { label: t('nav.menu.services.customDev'), path: '/services/custom-development' }
            ]
          },
          {
            title: t('nav.menu.services.operations'),
            titleLink: '/services/operations',
            items: [
              { label: t('nav.menu.services.logistics'), path: '/services/logistics' },
              { label: t('nav.menu.services.customerService'), path: '/services/customer-service' },
              { label: t('nav.menu.services.regulatory'), path: '/services/compliance' },
              { label: t('nav.menu.services.adminFinance'), path: '/services/admin-finance' }
            ]
          },
          {
            title: t('nav.menu.services.platforms'),
            titleLink: '/platforms',
            items: [
              { label: t('nav.menu.services.commerceIntelligence'), path: '/platforms/commerce-intelligence' }
            ]
          }
        ]
      }
    },
    {
      label: t('nav.whoWeAre'),
      path: '/about',
      hasMegaMenu: true,
      megaMenu: {
        title: t('nav.menu.about.title'),
        link: '/about',
        description: t('nav.menu.about.description'),
        mainLinks: [
          { label: t('nav.menu.about.aboutUs'), path: '/about' },
          { label: t('nav.menu.about.leaders'), path: '/about/leadership' },
          { label: t('nav.menu.about.clients'), path: '/about/clients' },
          { label: t('nav.menu.about.awards'), path: '/about/awards' },
          { label: t('nav.menu.about.newsroom'), path: '/news' },
          { label: t('nav.menu.about.connect'), path: '/contact' }
        ],
        newsSection: {
          title: t('nav.menu.about.newsTitle'),
          items: Array.isArray(aboutNews) ? [
            { ...aboutNews[0], path: '/news/new-cro' },
            { ...aboutNews[1], path: '/news/commerce-platform' },
            { ...aboutNews[2], path: '/news/logistics-facility' }
          ] : []
        }
      }
    },
    {
      label: t('nav.ourInsights'),
      path: '/insights',
      hasMegaMenu: true,
      megaMenu: {
        title: t('nav.menu.insights.title'),
        link: '/insights',
        description: t('nav.menu.insights.description'),
        mainLinks: [
          { label: t('nav.menu.insights.blogs'), path: '/insights?type=blog' },
          { label: t('nav.menu.insights.caseStudies'), path: '/case-studies' },
          { label: t('nav.menu.insights.ceo'), path: '/insights/ceo-perspective' },
          { label: t('nav.menu.insights.reports'), path: '/insights?type=report' },
          { label: t('nav.menu.insights.podcast'), path: '/podcast' }
        ],
        newsSection: {
          title: t('nav.menu.insights.newsTitle'),
          items: Array.isArray(insightsNews) ? [
            { ...insightsNews[0], path: '/insights/omnichannel-trends' },
            { ...insightsNews[1], path: '/insights/ecommerce-boom' },
            { ...insightsNews[2], path: '/insights/consumer-trends' }
          ] : []
        }
      }
    },
    {
      label: t('nav.careers'),
      path: '/careers',
      hasMegaMenu: false
    }
  ];

  const handleMouseEnter = (index) => {
    if (menuItems[index].hasMegaMenu) {
      setActiveDropdown(index);
    }
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const renderColumn = (column, idx) => (
    <div key={idx} className="mega-menu-column">
      <MenuLink to={column.titleLink} className="column-title">
        {column.title}
      </MenuLink>
      {column.subtitle && <p className="column-subtitle">{column.subtitle}</p>}
      <ul className="column-items">
        {column.items.map((item, itemIdx) => (
          <li key={itemIdx}>
            <MenuLink to={item.path}>{item.label}</MenuLink>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderMegaMenuWhatWeDo = (menu) => (
    <div className="mega-menu mega-menu-services">
      <div className="mega-menu-container">
        <div className="mega-menu-header">
          <Link to={menu.link} className="mega-menu-title">
            {menu.title}
            <FiChevronRight className="title-arrow" />
          </Link>
        </div>

        <div className="mega-menu-columns">
          {menu.columns.map(renderColumn)}
        </div>

        <div className="mega-menu-bottom-row">
          {menu.bottomRow.map(renderColumn)}
        </div>
      </div>
    </div>
  );

  const renderMegaMenuWithNews = (menu) => (
    <div className="mega-menu mega-menu-about">
      <div className="mega-menu-container">
        <div className="mega-menu-split">
          <div className="mega-menu-left">
            <Link to={menu.link} className="mega-menu-title">
              {menu.title}
            </Link>
            <p className="mega-menu-description">{menu.description}</p>
            <ul className="mega-menu-links">
              {menu.mainLinks.map((link, idx) => (
                <li key={idx}>
                  <Link to={link.path}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mega-menu-right">
            <h4 className="news-section-title">{menu.newsSection.title}</h4>
            <div className="news-items">
              {menu.newsSection.items.map((news, idx) => (
                <Link key={idx} to={news.path} className="news-item">
                  <h5>{news.title}</h5>
                  <span className="news-date">{news.date}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`} ref={dropdownRef}>
        <div className="header-container">
          <nav className="nav">
            <Link to="/" className="logo">
              <img src="/images/logo-mark.svg" alt={t('company.fullName')} className="logo-image" />
              <div className="logo-text-container">
                <span className="logo-company-name">{t('company.name')}</span>
                <span className="logo-company-type">{t('company.type')}</span>
              </div>
            </Link>

            <ul className="nav-menu">
              {menuItems.map((item, index) => (
                <li
                  key={index}
                  className={`nav-item ${item.hasMegaMenu ? 'has-dropdown' : ''} ${activeDropdown === index ? 'active' : ''}`}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link to={item.path} className="nav-link">
                    {item.label}
                    {item.hasMegaMenu && <FiChevronDown className="dropdown-icon" />}
                  </Link>

                  {item.hasMegaMenu && activeDropdown === index && (
                    item.megaMenu.columns
                      ? renderMegaMenuWhatWeDo(item.megaMenu)
                      : renderMegaMenuWithNews(item.megaMenu)
                  )}
                </li>
              ))}
            </ul>

            <div className="nav-actions">
              <Link to="/contact" className="btn btn-primary nav-cta">
                {t('nav.letsTalk')}
              </Link>
              <button
                className="search-toggle"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                aria-label="Search"
              >
                <FiSearch />
              </button>
            </div>

            <button
              className="mobile-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <FiX /> : <FiMenu />}
            </button>
          </nav>

          {isSearchOpen && (
            <div className="search-overlay">
              <div className="search-container">
                <input type="text" placeholder="Search..." className="search-input" autoFocus />
                <button className="search-close" onClick={() => setIsSearchOpen(false)}>
                  <FiX />
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {createPortal(
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-content">
            {menuItems.map((item, index) => (
              <div key={index} className="mobile-nav-item">
                <Link to={item.path} className="mobile-nav-link">{item.label}</Link>
                {item.hasMegaMenu && item.megaMenu.columns && (
                  <div className="mobile-submenu">
                    {item.megaMenu.columns.map((col, colIdx) => (
                      <div key={colIdx} className="mobile-submenu-group">
                        <MenuLink to={col.titleLink} className="mobile-submenu-title">
                          {col.title}
                        </MenuLink>
                        {col.items.map((subItem, subIdx) => (
                          <MenuLink key={subIdx} to={subItem.path} className="mobile-submenu-link">
                            {subItem.label}
                          </MenuLink>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {item.hasMegaMenu && item.megaMenu.mainLinks && (
                  <div className="mobile-submenu">
                    {item.megaMenu.mainLinks.map((link, linkIdx) => (
                      <Link key={linkIdx} to={link.path} className="mobile-submenu-link">
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mobile-nav-item">
              <Link to="/contact" className="btn btn-primary mobile-cta">{t('nav.letsTalk')}</Link>
            </div>
          </div>
        </div>,
        document.body
      )}

      <LanguageSelector />
    </>
  );
};

export default Header;
